import React, { useEffect, useMemo, useRef } from 'react';
import { Card, Descriptions } from 'antd';
import type { ColumnsType } from "antd/es/table";
import { useTable } from "@refinedev/antd";
import { parseAsInteger, parseAsString, useQueryStates } from "nuqs";
import { buildAliasMaps, MeterReadingsTable } from './MeterReadingsTable';
import { getReadoutFields } from "./readoutFormat";
import {
    encodeSortStateForQuery,
    extractSortStateFromTableSorter,
    isSameSortState,
    parseSortStateFromQuery,
    type SortState,
    toHeaderSortOrder,
} from "./tableSortUrlState";

interface CurrentReadingsTableProps {
    concentratorID: string;
    media: string;
    columns: ColumnsType<MeterCurrentRow>;
    searchValue: string;
}

interface MeterCurrentRow {
    id: number;
    latest_readout?: {
        values?: Record<string, number>;
        fields?: Record<string, unknown>;
    };
}

const DEFAULT_SORT_STATE: SortState = {
    key: 'name',
    order: 'asc',
};

const extractColumnKey = (column: ColumnsType<MeterCurrentRow>[number]): string | null => {
    if (!column) {
        return null;
    }

    if ('key' in column && column.key != null) {
        return String(column.key);
    }

    if ('dataIndex' in column && column.dataIndex != null) {
        if (Array.isArray(column.dataIndex)) {
            return column.dataIndex.join('.');
        }
        return String(column.dataIndex);
    }

    return null;
};

const isSortableColumn = (column: ColumnsType<MeterCurrentRow>[number]): boolean => {
    return 'sorter' in column && Boolean(column.sorter);
};

const defaultColumnsByMedia: Record<string, Array<{ key: string; visible: boolean }>> = {
    electricity: [
        { key: "name", visible: true },
        { key: "status", visible: true },
        { key: "latest_active_value", visible: true },
        { key: "latest_reactive_value", visible: true },
        { key: "latest_active_timestamp", visible: true },
        { key: "uuid", visible: false },
        { key: "address", visible: false },
        { key: "serial", visible: false },
    ],
    water: [
        { key: "name", visible: true },
        { key: "status", visible: true },
        { key: "latest_readout_value", visible: true },
        { key: "latest_readout_timestamp", visible: true },
        { key: "hot", visible: true },
        { key: "battery_value", visible: false },
        { key: "uuid", visible: false },
        { key: "address", visible: false },
        { key: "serial", visible: false },
    ],
    heat: [
        { key: "name", visible: true },
        { key: "status", visible: true },
        { key: "latest_readout_value_dual", visible: true },
        { key: "latest_readout_timestamp", visible: true },
        { key: "uuid", visible: false },
        { key: "address", visible: false },
        { key: "serial", visible: false },
    ],
    all: [
        { key: "name", visible: true },
        { key: "status", visible: true },
        { key: "primary_value", visible: true },
        { key: "latest_readout_timestamp", visible: true },
        { key: "media", visible: true },
        { key: "uuid", visible: false },
        { key: "address", visible: false },
        { key: "serial", visible: false },
    ],
};

export const CurrentReadingsTable: React.FC<CurrentReadingsTableProps> = ({
    concentratorID,
    media,
    columns,
    searchValue,
}) => {
    const hasAppliedInitialSearchFilter = useRef(false);
    const [tableQuery, setTableQuery] = useQueryStates(
        {
            p: parseAsInteger.withDefault(1),
            ps: parseAsInteger.withDefault(10),
            s: parseAsString,
        },
        { history: "push" }
    );

    const sortableColumnKeys = useMemo(() => {
        const seen = new Set<string>();
        return columns
            .filter((column) => isSortableColumn(column))
            .map((column) => extractColumnKey(column))
            .filter((key): key is string => Boolean(key))
            .filter((key) => {
                if (seen.has(key)) {
                    return false;
                }
                seen.add(key);
                return true;
            });
    }, [columns]);

    const sortableColumnConfig = useMemo(
        () => sortableColumnKeys.map((key) => ({ key, visible: true })),
        [sortableColumnKeys]
    );

    const { aliasByKey, keyByAlias } = useMemo(
        () => buildAliasMaps(sortableColumnConfig),
        [sortableColumnConfig]
    );

    const sortFromQuery = useMemo(
        () => parseSortStateFromQuery(tableQuery.s, keyByAlias),
        [keyByAlias, tableQuery.s]
    );

    const activeSortState = sortFromQuery ?? DEFAULT_SORT_STATE;

    const columnsWithSortState = useMemo(() => {
        return columns.map((column) => {
            if (!isSortableColumn(column)) {
                return column;
            }

            const key = extractColumnKey(column);
            if (!key) {
                return column;
            }

            return {
                ...column,
                sortOrder:
                    key === activeSortState.key ? toHeaderSortOrder(activeSortState.order) : null,
            };
        });
    }, [activeSortState.key, activeSortState.order, columns]);

    useEffect(() => {
        if (!tableQuery.s) {
            return;
        }

        if (!sortFromQuery || isSameSortState(sortFromQuery, DEFAULT_SORT_STATE)) {
            void setTableQuery({ s: null }, { history: "replace" });
        }
    }, [setTableQuery, sortFromQuery, tableQuery.s]);

    const tableOptions = useMemo(
        () => ({
            resource: `concentrators/${concentratorID}/meters/${media}`,
            pagination: {
                currentPage: tableQuery.p,
                pageSize: tableQuery.ps,
            },
            syncWithLocation: false,
            filters: {
                mode: "server" as const,
                defaultBehavior: "merge" as const,
                initial: [
                    {
                        field: "search",
                        operator: "eq" as const,
                        value: searchValue,
                    },
                ],
            },
            sorters: {
                initial: [
                    {
                        field: activeSortState.key,
                        order: activeSortState.order,
                    },
                ],
            },
        }),
        [
            activeSortState.key,
            activeSortState.order,
            concentratorID,
            media,
            searchValue,
            tableQuery.p,
            tableQuery.ps,
        ]
    );

    const { tableProps, setFilters } = useTable<MeterCurrentRow>(tableOptions);

    // Apply search filter when searchValue changes
    useEffect(() => {
        if (!hasAppliedInitialSearchFilter.current) {
            hasAppliedInitialSearchFilter.current = true;
            return;
        }

        setFilters([
            {
                field: "search",
                operator: "eq" as const,
                value: searchValue, // Always send value, backend handles empty string as "show all"
            }
        ]);
    }, [searchValue, setFilters]);

    return (
        <MeterReadingsTable<MeterCurrentRow>
            tableKey={`meters-${concentratorID}-${media}-current`}
            tableProps={tableProps}
            columns={columnsWithSortState}
            rowKey="id"
            defaultColumns={defaultColumnsByMedia[media]}
            columnStateMode="url"
            pagination={{
                ...tableProps.pagination,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100', '200', '500'],
                showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} items`,
            }}
            expandable={{
                rowExpandable: (record) =>
                    Boolean(
                        (record.latest_readout?.values &&
                            Object.keys(record.latest_readout.values).length > 0) ||
                        Object.keys(getReadoutFields(record)).length > 0
                    ),
                expandedRowRender: (record) => (
                    <Card size="small">
                        <Descriptions column={1} size="small" bordered>
                            <Descriptions.Item label="Values">
                                <pre style={{ margin: 0 }}>
                                    {JSON.stringify(record.latest_readout?.values ?? {}, null, 2)}
                                </pre>
                            </Descriptions.Item>
                            <Descriptions.Item label="Fields">
                                <pre style={{ margin: 0 }}>
                                    {JSON.stringify(getReadoutFields(record), null, 2)}
                                </pre>
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>
                ),
            }}
            scroll={{ x: 1200 }}
            onChange={(paginationState, _filters, sorter) => {
                const nextPage = paginationState.current ?? 1;
                const nextPageSize = paginationState.pageSize ?? 10;
                const nextSortState = extractSortStateFromTableSorter(sorter);
                const hasKnownSortKey = nextSortState
                    ? aliasByKey.has(nextSortState.key)
                    : false;
                const nextSortQuery = nextSortState && hasKnownSortKey
                    ? encodeSortStateForQuery(nextSortState, aliasByKey)
                    : null;

                const normalizedSortQuery = nextSortState && hasKnownSortKey && !isSameSortState(nextSortState, DEFAULT_SORT_STATE)
                    ? nextSortQuery
                    : null;

                void setTableQuery(
                    {
                        p: nextPage === 1 ? null : nextPage,
                        ps: nextPageSize === 10 ? null : nextPageSize,
                        s: normalizedSortQuery,
                    },
                    { history: "push" }
                );
            }}
        />
    );
};
